import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

// eslint-disable-next-line max-lines-per-function
function RollbackConfirmationDialog({
  open,
  onClose,
  onConfirm,
  importResults,
  isRollingBack = false,
  rollbackProgress = null
}) {
  const [showEntityList, setShowEntityList] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const createdCount = importResults?.successful || 0;
  const requiredConfirmText = `DELETE ${createdCount} ENTITIES`;
  const isConfirmValid = confirmText === requiredConfirmText;

  const handleConfirm = () => {
    if (isConfirmValid) {
      onConfirm();
    }
  };

  const handleClose = () => {
    if (!isRollingBack) {
      setConfirmText('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isRollingBack}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" />
          <Typography variant="h6">
            {isRollingBack ? 'Rolling Back Import...' : 'Confirm Import Rollback'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {isRollingBack ? (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Rollback in progress. This operation cannot be cancelled once started.
              </Typography>
            </Alert>

            {rollbackProgress && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Progress: {rollbackProgress.deletedCount} of {rollbackProgress.totalToDelete} entities deleted
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Permanently deleting entities and their permissions...
                  </Typography>
                </Box>

                {rollbackProgress.failedDeletes && rollbackProgress.failedDeletes.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      Failed to delete {rollbackProgress.failedDeletes.length} entities.
                      These may need to be removed manually.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                ⚠️ PERMANENT DELETION WARNING
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                This action will <strong>permanently delete all {createdCount} entities</strong> created
                during this import, along with their permissions and associated data.
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                This action cannot be undone.
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Import Summary:
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip
                  label={`${createdCount} Entities Created`}
                  color="success"
                  size="small"
                />
                {importResults?.updated > 0 && (
                  <Chip
                    label={`${importResults.updated} Updated`}
                    color="info"
                    size="small"
                  />
                )}
                {importResults?.failed > 0 && (
                  <Chip
                    label={`${importResults.failed} Failed`}
                    color="error"
                    size="small"
                  />
                )}
              </Box>

              <Button
                size="small"
                onClick={() => setShowEntityList(!showEntityList)}
                endIcon={showEntityList ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ mb: 2 }}
              >
                {showEntityList ? 'Hide' : 'Show'} Entity Preview
              </Button>

              <Collapse in={showEntityList}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Entities to be deleted (showing first 10):
                  </Typography>
                </Alert>

                <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                  {/* Note: This would need entity data from import results */}
                  {Array.from({ length: Math.min(createdCount, 10) }).map((_, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={`Entity ${index + 1}`}
                        secondary="Person entity created during import"
                      />
                    </ListItem>
                  ))}
                  {createdCount > 10 && (
                    <ListItem>
                      <ListItemText
                        primary={`... and ${createdCount - 10} more entities`}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                To confirm this destructive action, please type the following text exactly:
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 'bold',
                  bgcolor: 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  mb: 2
                }}
              >
                {requiredConfirmText}
              </Typography>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type confirmation text here"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  border: isConfirmValid ? '2px solid #4caf50' : '2px solid #ccc',
                  borderRadius: '4px',
                  outline: 'none'
                }}
              />
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleClose}
          disabled={isRollingBack}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>

        {!isRollingBack && (
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={!isConfirmValid}
            startIcon={<DeleteIcon />}
          >
            Permanently Delete {createdCount} Entities
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default RollbackConfirmationDialog;